import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select";
import { useProfils } from "@repo/ui/hooks/api/profil";
import { cn } from "@repo/ui/lib/utils";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Shield,
  User,
  X,
} from "lucide-react";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  PASSWORD_RULES,
  userCreateFormSchema,
  type UserFormSchema,
} from "../../user/hooks/user-form-schema";

export interface UserMiniFormHandle {
  validate: () => Promise<boolean>;
  getData: () => UserFormSchema;
}

interface UserMiniFormProps {
  index: number;
  groupeId: number;
  onRemove: () => void;
  showRemove: boolean;
}

export const UserMiniForm = forwardRef<UserMiniFormHandle, UserMiniFormProps>(
  ({ index, groupeId, onRemove, showRemove }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    const { data: profilsData, isLoading: isLoadingProfils } = useProfils({
      page: 1,
      limit: 100,
      sortBy: "recent",
      includeDeleted: false,
    });

    const form = useForm<UserFormSchema>({
      resolver: zodResolver(userCreateFormSchema),
      defaultValues: {
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        profil_id: 0,
        groupe_id: groupeId,
        is_active: true,
        email_active: false,
      },
      mode: "onChange",
    });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        const result = await form.trigger();
        return result;
      },
      getData: () => ({
        ...form.getValues(),
        groupe_id: groupeId,
      }),
    }));

    const password = form.watch("password") ?? "";
    const username = form.watch("username");
    const email = form.watch("email");
    const hasContent = username || email;

    const {
      formState: { errors },
    } = form;

    const hasErrors = Object.keys(errors).length > 0;

    return (
      <div
        className={cn(
          "rounded-xl border-2 transition-all duration-200",
          hasErrors
            ? "border-destructive/40 bg-destructive/3"
            : "border-border/60 bg-card hover:border-border"
        )}
      >
        {/* Card header */}
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
          onClick={() => setIsExpanded((v) => !v)}
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold shrink-0",
                hasErrors
                  ? "bg-destructive/15 text-destructive"
                  : hasContent
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground"
              )}
            >
              {index + 1}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium leading-none">
                {username
                  ? username
                  : `Utilisateur ${index + 1}`}
              </span>
              {email && (
                <span className="text-xs text-muted-foreground mt-0.5">
                  {email}
                </span>
              )}
            </div>
            {hasErrors && (
              <Badge
                variant="destructive"
                className="text-[10px] px-1.5 py-0 h-4"
              >
                Erreurs
              </Badge>
            )}
            {!hasErrors && hasContent && (
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/15">
                <Check className="h-2.5 w-2.5 text-primary" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {showRemove && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </div>
        </div>

        {/* Collapsible body */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-border/40 pt-4">
            {/* Row 1: username + email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Nom d&apos;utilisateur *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Controller
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="jean.dupont"
                        className={cn(
                          "pl-8 h-9 text-sm",
                          errors.username && "border-destructive focus-visible:ring-destructive/30"
                        )}
                      />
                    )}
                  />
                </div>
                {errors.username && (
                  <p className="text-[11px] text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Email *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Controller
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="jean.dupont@mairie.fr"
                        className={cn(
                          "pl-8 h-9 text-sm",
                          errors.email && "border-destructive focus-visible:ring-destructive/30"
                        )}
                      />
                    )}
                  />
                </div>
                {errors.email && (
                  <p className="text-[11px] text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: password + confirmPassword */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Mot de passe *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Controller
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className={cn(
                          "pl-8 pr-9 h-9 text-sm",
                          errors.password && "border-destructive focus-visible:ring-destructive/30"
                        )}
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                {/* Inline password rules */}
                {password.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 pt-1">
                    {PASSWORD_RULES.map((rule) => {
                      const ok = rule.isSatisfied(password);
                      return (
                        <span
                          key={rule.id}
                          className={cn(
                            "text-[10px] flex items-center gap-1 transition-colors",
                            ok ? "text-emerald-600" : "text-muted-foreground"
                          )}
                        >
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full shrink-0",
                              ok ? "bg-emerald-500" : "bg-muted-foreground/40"
                            )}
                          />
                          {rule.label}
                        </span>
                      );
                    })}
                  </div>
                )}
                {errors.password && !password.length && (
                  <p className="text-[11px] text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Confirmation *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Controller
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type={showConfirm ? "text" : "password"}
                        placeholder="••••••••"
                        className={cn(
                          "pl-8 pr-9 h-9 text-sm",
                          errors.confirmPassword && "border-destructive focus-visible:ring-destructive/30"
                        )}
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirm((v) => !v)}
                  >
                    {showConfirm ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-[11px] text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            {/* Row 3: profil + is_active */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Shield className="h-3 w-3" />
                  Profil *
                </Label>
                <Controller
                  control={form.control}
                  name="profil_id"
                  render={({ field }) => (
                    <Select
                      value={field.value ? String(field.value) : ""}
                      onValueChange={(v) => field.onChange(Number(v))}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-9 text-sm",
                          errors.profil_id && "border-destructive"
                        )}
                      >
                        <SelectValue
                          placeholder={
                            isLoadingProfils
                              ? "Chargement..."
                              : "Sélectionner un profil"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(profilsData?.data ?? []).map((profil) => (
                          <SelectItem key={profil.id} value={String(profil.id)}>
                            {profil.name ?? "Sans nom"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.profil_id && (
                  <p className="text-[11px] text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.profil_id.message}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 pb-1">
                <Controller
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <Checkbox
                      id={`is_active_${index}`}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label
                  htmlFor={`is_active_${index}`}
                  className="text-sm cursor-pointer select-none"
                >
                  Compte actif
                </Label>
              </div>
            </div>

            {/* Global form error */}
            {hasErrors && (
              <Alert variant="destructive" className="py-2 text-sm">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Veuillez corriger les erreurs avant de continuer.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>
    );
  }
);

UserMiniForm.displayName = "UserMiniForm";
